import type { TFunction } from "i18next";
import { useState, useRef, useEffect, useCallback, type MutableRefObject } from "react";
import type { Edge, Node } from "@xyflow/react";
import type { AxiosInstance } from "axios";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { useU2000LUStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000LUHandlers";
import { U2000LUStateEnumNextAction } from "../../../../../u2000Core/reducer/formReducer/U2000LUState";
import type { NavigateFunction } from "react-router-dom";
import type { U3141ChildLotChain, U3141LotDetailOperationTypeFromInitLU } from "../../../../U3141Service";
import { NS } from "../../../../U3141";
import { u3141BuildGraphNodeDisplayData } from "../../../../tools/u3141GraphNodeDisplayData";
import { u3141BuildLotChainGraphEdges } from "../../../../tools/u3141BuildLotChainGraphEdges";
import { u3141ApplyHierarchyLayoutToLotChainGraph } from "../../../../tools/u3141LayoutGraphByHierarchy";
import { u3141GraphNodePositionsAreDegenerate } from "../../../../tools/u3141GraphNodePositionsAreDegenerate";
import { u3141SyncChildLotChainDocumentTypesFromDocumentsList } from "../../../../tools/u3141SyncChildLotChainDocumentTypesFromDocumentsList";
import type { U3141LotDetailDocument } from "../../../../U3141Service";

/** Retire l’entrée « chaîne de lots annotée » du lot pour mise à jour optimiste avant refetch lotChainGraph. */
function u3141StripRfLotDetailAnnotatedLotChainFromChildLotChain(clc: U3141ChildLotChain): U3141ChildLotChain {
  const dts = clc.lotDetail?.documentTypes;
  if (!clc.lotDetail || !Array.isArray(dts) || dts.length === 0) {
    return clc;
  }
  const filtered = dts.filter((d) => d?.documentType !== "rfLotDetailAnnotatedLotChain");
  if (filtered.length === dts.length) {
    return clc;
  }
  return {
    ...clc,
    lotDetail: {
      ...clc.lotDetail,
      documentTypes: filtered,
    },
  };
}

// Gestion des handlers pour les actions d'une unité logique
export interface U3141MasterChildLotChainHandler {
  onRowClick: (role: U3141ChildLotChain) => void;
  onAddChildLotChain: () => void;
  onModifyChildLotChain: () => void;
  onViewLotchain(): void;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3141ChildLotChain>>;
  setDataChangedEvent: (dataChangedEvent: number) => void;
  dataChangedEvent: number;

  isReactFlowDialogOpen: boolean;
  setReactFlowDialogOpen: (isOpen: boolean) => void;
  initialNodes: Node[];
  initialEdges: Edge[];
  /** Reconstruit le graphe à partir des données de la grille (sans positions sauvegardées). À appeler après reset des positions. */
  refreshInitialGraphFromGrid: (data: U3141ChildLotChain[]) => void;
  /** Applique un snapshot lotChainGraph (POST create BL-0661) en conservant nodePosition. */
  applyLotChainGraphSnapshot: (data: U3141ChildLotChain[]) => void;
  /** BL-0697 — snapshot après create : layout des nouveaux nœuds seulement (canvas inchangé). */
  applyLotChainGraphSnapshotFromGraphCreate: (data: U3141ChildLotChain[]) => void;
  /** Clé layout initiale U2000ReactFlowDialog (ouverture dialog). */
  graphInitialLayoutKey: number;
  /** BL-0707 — incrémente la clé pour forcer un relayout sans merge canvas. */
  bumpGraphInitialLayoutKey: () => void;
  /** BL-0707 — GET / reset / reconstitute : layout hiérarchique complet sur le snapshot. */
  refreshInitialGraphWithFullHierarchyLayout: (data: U3141ChildLotChain[]) => void;
  /** Ref alimentée par U2000ReactFlowDialog — positions canvas au moment du create graphe. */
  graphLiveNodesRef: MutableRefObject<Node[]>;
  /** BL-0711 — signal U2000ReactFlowDialog pour resync baseline positions après create lot. */
  graphPositionsBaselineCommitKey: number;
  bumpGraphPositionsBaselineCommitKey: () => void;
  /** Construit le graphe à partir des données fournies (ex. réponse API lotChainGraph) et ouvre le dialog. */
  loadAndOpenLotChainGraph: (data: U3141ChildLotChain[]) => void;
  /**
   * Retire l’entrée document rfLotDetailAnnotatedLotChain du nœud (données graphe) après « Générer chaîne de lots annotée »,
   * en attendant le repeuplement via GET lotChainGraph à la fin du job.
   */
  removeAnnotatedLotChainDocumentFromGraphNode: (childLotChainId: string) => void;
  /** Met à jour les libellés d’un nœud lot après POST update (BL-0662). */
  patchGraphNodeFromChildLotChain: (child: U3141ChildLotChain) => void;
  /** BL-0702 — sync noms/types documents menu graphe après CRUD dialogue « Gérer les documents ». */
  patchGraphNodeDocumentTypesFromDocumentsList: (childLotChainId: string, documents: U3141LotDetailDocument[]) => void;
}

/** Alias du type retourné par le hook (évite la duplication d’interface avec `U3141MasterChildLotChainHandler`). */
export type U3141MasterChildLotChainHandlerReturn = U3141MasterChildLotChainHandler;

// Paramètres pour les handlers des actions d'une unité logique
export interface U3141MasterChildLotChainHandlerParam {
  u2000AxiosAuthenticated: AxiosInstance;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3141ChildLotChain>>;
  t: TFunction;
  organizationId: string;
  navigate: NavigateFunction;
  /** Langue courante (ex. "fr", "en") pour la traduction du type d'opération dans le graphe. */
  language?: string;
  /** Types d'opération initLU (libellés et drapeau info supplémentaire). */
  lotDetailOperationTypes?: U3141LotDetailOperationTypeFromInitLU[];
}

// Implémentation des handlers pour les actions d'une unité logique (hook React)
export function useU3141MasterChildLotChainHandler(params: U3141MasterChildLotChainHandlerParam): U3141MasterChildLotChainHandlerReturn {
  const { luStateHandlers, gridStateHandlers, navigate, language = "fr", lotDetailOperationTypes, t } = params;
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);
  const [localEditMode] = useState<U2000FormStateEnumEditMode>(U2000FormStateEnumEditMode.NONE);
  const localEditModeRef = useRef(localEditMode);
  const [isReactFlowDialogOpen, setReactFlowDialogOpen] = useState(false);
  const [initialNodes, setInitialNodes] = useState<Node[]>([]);
  const [initialEdges, setInitialEdges] = useState<Edge[]>([]);
  const [graphInitialLayoutKey, setGraphInitialLayoutKey] = useState(0);
  const [graphPositionsBaselineCommitKey, setGraphPositionsBaselineCommitKey] = useState(0);
  const bumpGraphInitialLayoutKey = useCallback(() => {
    setGraphInitialLayoutKey((k) => k + 1);
  }, []);
  const bumpGraphPositionsBaselineCommitKey = useCallback(() => {
    setGraphPositionsBaselineCommitKey((k) => k + 1);
  }, []);
  /** Positions React Flow courantes (dialog) — source de vérité pour préserver le layout au create graphe. */
  const graphLiveNodesRef = useRef<Node[]>([]);

  useEffect(() => {
    localEditModeRef.current = localEditMode;
  }, [localEditMode]);

  // Gestion du clic sur une ligne du tableau des rôles
  const onRowClick = (childLotChain: U3141ChildLotChain) => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
    luStateHandlers.setActionOriginFrom(`/U3141/CHILDLOTCHAIN`);
    const currentIdx = gridStateHandlers ? gridStateHandlers.getData().findIndex((r) => r.id === childLotChain.id) : -1;
    if (gridStateHandlers) {
      gridStateHandlers.setCurrentIdx(currentIdx);
    }
    navigate(`childLotChain/${childLotChain.id}`);
  };

  // Gestion de l'ajout d'une action d'unité logique
  const onAddChildLotChain = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
    luStateHandlers.setActionOriginFrom(`/U3141/CHILDLOTCHAIN`);
    navigate("childLotChain/new");
  };

  // Gestion de la modification d'une action d'unité logique
  const onModifyChildLotChain = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
    luStateHandlers.setActionOriginFrom(`/U3141/CHILDLOTCHAIN`);
    navigate(`childLotChain/${gridStateHandlers.getSelectedRows()[0]?.id}`);
  };

  const prepareGraphRowsForOpen = useCallback((data: U3141ChildLotChain[]): U3141ChildLotChain[] => {
    const rows = Array.isArray(data) ? data : [];
    if (rows.length === 0 || !u3141GraphNodePositionsAreDegenerate(rows)) {
      return rows;
    }
    return u3141ApplyHierarchyLayoutToLotChainGraph(rows, {});
  }, []);

  const onViewLotchain = (): void => {
    const raw = gridStateHandlers.getData();
    const wasOpen = isReactFlowDialogOpen;
    const degenerate = u3141GraphNodePositionsAreDegenerate(raw);
    createInitialGraphFromGrid(prepareGraphRowsForOpen(raw));
    if (wasOpen && degenerate) {
      bumpGraphInitialLayoutKey();
    }
    setReactFlowDialogOpen(true);
  };

  /**
   * Génère nodes et edges pour React Flow à partir de gridDetailState.data.
   * Liens parent → enfant dérivés de lotDetail.concordanceOrigines (array de lots) : pour chaque entrée qui référence un lot présent dans la chaîne, on crée un edge (un lot cible peut avoir plusieurs parents).
   * Si row.nodePosition est présent, le nœud reçoit cette position (sinon le dialog appliquera dagre).
   * @param {Array} data - tableau d'objets de la grille / réponse lotChainGraph
   */
  const createInitialGraphFromGrid = (data: U3141ChildLotChain[]) => {
    if (!Array.isArray(data)) return;
    const nodes: Node[] = data.map((row) => {
      const hasPosition = row.nodePosition && typeof row.nodePosition.x === "number" && typeof row.nodePosition.y === "number";
      return {
        id: String(row.id),
        position: hasPosition ? { x: row.nodePosition!.x, y: row.nodePosition!.y } : { x: 0, y: 0 },
        data: u3141BuildGraphNodeDisplayData(row, t, language, lotDetailOperationTypes),
        type: "u2000Complex",
      };
    });

    const edges = u3141BuildLotChainGraphEdges(data);

    setInitialNodes(nodes);
    setInitialEdges(edges);
  };

  const refreshInitialGraphFromGrid = (data: U3141ChildLotChain[]) => {
    createInitialGraphFromGrid(data.map((row) => ({ ...row, nodePosition: undefined })));
  };

  const refreshInitialGraphWithFullHierarchyLayout = useCallback((data: U3141ChildLotChain[]) => {
    const graph = Array.isArray(data) ? data : [];
    const laidOut = u3141ApplyHierarchyLayoutToLotChainGraph(graph, {});
    createInitialGraphFromGrid(laidOut);
  }, []);

  /** Snapshot lotChainGraph (POST create BL-0661) — conserve nodePosition API. */
  const applyLotChainGraphSnapshot = (data: U3141ChildLotChain[]) => {
    createInitialGraphFromGrid(Array.isArray(data) ? data : []);
  };

  /**
   * BL-0697 — met à jour le graphe après create : merge canvas (U2000ReactFlowDialog) pour les nœuds existants ;
   * placement hiérarchique uniquement pour les nouveaux ids (positions holders = canvas live).
   */
  const applyLotChainGraphSnapshotFromGraphCreate = (data: U3141ChildLotChain[]) => {
    const graph = Array.isArray(data) ? data : [];
    const liveById = new Map(graphLiveNodesRef.current.map((node) => [node.id, node]));
    const preservePositions: Record<string, { x: number; y: number }> = {};
    liveById.forEach((node, id) => {
      preservePositions[id] = { x: node.position.x, y: node.position.y };
    });
    const laidOut = u3141ApplyHierarchyLayoutToLotChainGraph(graph, { preservePositions });
    createInitialGraphFromGrid(laidOut);
    bumpGraphPositionsBaselineCommitKey();
  };

  const loadAndOpenLotChainGraph = (data: U3141ChildLotChain[]) => {
    createInitialGraphFromGrid(prepareGraphRowsForOpen(Array.isArray(data) ? data : []));
    setReactFlowDialogOpen(true);
  };

  const patchGraphNodeFromChildLotChain = useCallback(
    (child: U3141ChildLotChain) => {
      if (!child?.id) return;
      const childId = String(child.id);
      setInitialNodes((prev) =>
        prev.map((node) => {
          if (String(node.id) !== childId) {
            return node;
          }
          return {
            ...node,
            data: {
              ...node.data,
              ...u3141BuildGraphNodeDisplayData(child, t, language, lotDetailOperationTypes),
            },
          };
        }),
      );
    },
    [t, language, lotDetailOperationTypes],
  );

  const patchGraphNodeDocumentTypesFromDocumentsList = useCallback(
    (childLotChainId: string, documents: U3141LotDetailDocument[]) => {
      if (!childLotChainId) return;
      setInitialNodes((prev) =>
        prev.map((node) => {
          const payload = node.data?.nodePayload as U3141ChildLotChain | undefined;
          if (!payload || String(payload.id) !== String(childLotChainId)) {
            return node;
          }
          const nextPayload = u3141SyncChildLotChainDocumentTypesFromDocumentsList(payload, documents);
          return {
            ...node,
            data: {
              ...node.data,
              ...u3141BuildGraphNodeDisplayData(nextPayload, t, language, lotDetailOperationTypes),
              nodePayload: nextPayload as unknown,
            },
          };
        }),
      );
    },
    [t, language, lotDetailOperationTypes],
  );

  const removeAnnotatedLotChainDocumentFromGraphNode = useCallback(
    (childLotChainId: string) => {
      if (!childLotChainId) return;
      setInitialNodes((prev) =>
        prev.map((node) => {
          const payload = node.data?.nodePayload as U3141ChildLotChain | undefined;
          if (!payload || String(payload.id) !== String(childLotChainId)) {
            return node;
          }
          const nextPayload = u3141StripRfLotDetailAnnotatedLotChainFromChildLotChain(payload);
          return {
            ...node,
            data: {
              ...node.data,
              ...u3141BuildGraphNodeDisplayData(nextPayload, t, language, lotDetailOperationTypes),
              nodePayload: nextPayload as unknown,
            },
          };
        }),
      );
    },
    [t, language, lotDetailOperationTypes],
  );

  return {
    onRowClick,
    onAddChildLotChain,
    onModifyChildLotChain,
    onViewLotchain,

    gridStateHandlers,
    setDataChangedEvent,
    dataChangedEvent,

    isReactFlowDialogOpen,
    setReactFlowDialogOpen,
    initialNodes,
    initialEdges,
    refreshInitialGraphFromGrid,
    applyLotChainGraphSnapshot,
    applyLotChainGraphSnapshotFromGraphCreate,
    graphInitialLayoutKey,
    bumpGraphInitialLayoutKey,
    refreshInitialGraphWithFullHierarchyLayout,
    graphLiveNodesRef,
    graphPositionsBaselineCommitKey,
    bumpGraphPositionsBaselineCommitKey,
    loadAndOpenLotChainGraph,
    removeAnnotatedLotChainDocumentFromGraphNode,
    patchGraphNodeFromChildLotChain,
    patchGraphNodeDocumentTypesFromDocumentsList,
  };
}
